import type { CSSProperties, FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PointCloud } from '@/components/generals/point-cloud'
import { ContactForm } from '@/components/contact/contact-form'
import {
    GLOBE_RADIUS,
    latLon,
    LISBON,
    STOCKHOLM
} from '@/lib/cloud/shapes/globe'

import { OFFICES } from '@/constants/navigation'
import { STUDIO_EMAIL } from '@/constants/studio'

const GLOBE_TAGS = [
    { key: 'sto', text: 'Stockholm', anchor: latLon(STOCKHOLM.lat, STOCKHOLM.lon, GLOBE_RADIUS * 1.03), variant: 'side' },
    { key: 'lis', text: 'Lisbon', anchor: latLon(LISBON.lat, LISBON.lon, GLOBE_RADIUS * 1.03), variant: 'side' }
]

const GLOBE_ARC = {
    from: latLon(STOCKHOLM.lat, STOCKHOLM.lon, GLOBE_RADIUS),
    to: latLon(LISBON.lat, LISBON.lon, GLOBE_RADIUS),
    lift: 2.2
}

const delay = (value: number): CSSProperties => ({ animationDelay: `${value}ms` })

const ContactPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contact'
                description='Start with a scan. Send Norrvik a brief and a partner in Stockholm or Lisbon replies within two working days.'
                figure='Fig. 01 / Contact / New brief'
                shape='globe'
            />

            <div className='grid desk:grid-cols-2'>
                <section aria-labelledby='contact-title' className='border-b border-hairline px-gutter pb-16 pt-12 desk:border-b-0 desk:border-r desk:pb-24 desk:pt-20'>
                    <div data-ruler-mark='01' className='rise-fade' style={delay(0)}>
                        <p className='hud text-ink'>
                            Fig. 01 / Contact / New brief
                        </p>

                        <p className='hud text-mute'>
                            Every commission begins on site
                        </p>
                    </div>

                    <h1 id='contact-title' className='display-xl mt-10'>
                        <span className='rise-line'>
                            <span className='rise-inner text-ink' style={delay(80)}>
                                Start with
                            </span>
                        </span>

                        <span className='rise-line'>
                            <span className='rise-inner text-dim' style={delay(170)}>
                                a scan.
                            </span>
                        </span>
                    </h1>

                    <p className='rise-fade body-copy mt-8 text-ink/85' style={delay(320)}>
                        Tell us about the building and a partner in Stockholm or Lisbon will reply within two working days, usually with a first question about the site. You can also write to{' '}
                        <a href={`mailto:${STUDIO_EMAIL}`} className='text-ink underline decoration-hairline underline-offset-4 transition-colors duration-300 hover:decoration-ink'>
                            {STUDIO_EMAIL}
                        </a>
                        .
                    </p>

                    <ContactForm />
                </section>

                <section aria-labelledby='offices-heading' className='relative'>
                    <div className='desk:sticky desk:top-16'>
                        <PointCloud
                            shape='globe'
                            label='A slowly rotating globe made of points, with blue markers on Stockholm and Lisbon joined by a dashed arc that flows from one office to the other.'
                            budget='globe'
                            rotate='always'
                            rotateSpeed={6}
                            hoverBoost={2.5}
                            spin
                            tilt={0.38}
                            yaw={0}
                            pitch={12}
                            fitWidth={30}
                            effects={false}
                            tags={GLOBE_TAGS}
                            arc={GLOBE_ARC}
                            className='h-[420px] w-full border-b border-hairline desk:h-[calc(100svh-4rem-260px)] desk:min-h-[380px]'
                        >
                            <div className='pointer-events-none absolute inset-0 z-[2]' aria-hidden='true'>
                                <p className='hud absolute left-5 top-5 text-ink'>
                                    Fig. 02 / Offices / Great circle
                                </p>

                                <p className='hud absolute right-gutter top-5 text-right text-mute'>
                                    STO to LIS
                                    <span className='block text-ink'>
                                        2 989 km
                                    </span>
                                </p>
                            </div>
                        </PointCloud>

                        <h2 id='offices-heading' className='sr-only'>
                            Office addresses and phone numbers
                        </h2>

                        <div className='grid sm:grid-cols-2'>
                            {OFFICES.map(office => (
                                <div key={office.code} className='border-b border-hairline px-gutter py-6 sm:odd:border-r sm:last:odd:col-span-2 sm:last:odd:border-r-0 desk:pl-6'>
                                    <p className='hud text-ink'>
                                        {office.city}
                                        <span className='text-mute'>
                                            {' / '}
                                            {office.code}
                                        </span>
                                    </p>

                                    <address className='mt-3 text-[14px] not-italic leading-[1.55] text-ink/85'>
                                        {office.address.map(line => (
                                            <span key={line} className='block'>
                                                {line}
                                            </span>
                                        ))}
                                    </address>

                                    <a href={office.phoneHref} className='hud mt-3 inline-block text-ink underline decoration-hairline underline-offset-4 transition-colors duration-300 hover:decoration-ink'>
                                        {office.phone}
                                    </a>

                                    <p className='hud mt-1 text-mute'>
                                        {office.coords}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    )
}

export default ContactPage
